<template>
  <!--
    config is this wizard's single source of truth: EditSqlViewWidget passes it by
    reference and every step mutates it in place (each step is a view over the same
    object). That shared mutation is what preserves entered values across steps, so
    vue/no-mutating-props is intentionally disabled for this presentational step.
  -->
  <!-- eslint-disable vue/no-mutating-props -->
  <div class="wizard-step-behavior">
    <!-- Options for the chosen display type (legend for charts, grid options for grids) -->
    <section class="viz-section">
      <div class="viz-section__head">
        <span class="ds-field-label">
          Display Options
          <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
        </span>
      </div>
      <span class="viz-sub-label">Widget size</span>
      <v-btn-toggle v-model="config.widgetSize" mandatory dense>
        <v-btn small value="small"><v-icon x-small class="mr-1">mdi-format-font-size-decrease</v-icon> Small</v-btn>
        <v-btn small value="normal"><v-icon x-small class="mr-1">mdi-format-size</v-icon> Normal</v-btn>
        <v-btn small value="large"><v-icon x-small class="mr-1">mdi-format-font-size-increase</v-icon> Large</v-btn>
      </v-btn-toggle>

      <!-- Stacked-column layout (stackedColumn only): stack the series to a total, or
           render them side-by-side. Mirrors the widget-size toggle pattern. -->
      <template v-if="isStackedType">
        <span class="viz-sub-label mt-3">Bars</span>
        <v-btn-toggle v-model="config.stackMode" mandatory dense>
          <v-btn small value="stack"><v-icon x-small class="mr-1">mdi-chart-bar-stacked</v-icon> Stacked</v-btn>
          <v-btn small value="group"><v-icon x-small class="mr-1">mdi-chart-bar</v-icon> Grouped</v-btn>
        </v-btn-toggle>
      </template>

      <!-- Dual-axis line: assign each calculation to the left or right value axis
           (default: first calculation left, second right). -->
      <template v-if="isDualAxisType">
        <span class="viz-sub-label mt-3">Axis assignment</span>
        <div v-if="!dualAxisMetrics.length" class="viz-sub-hint">
          Add at least two calculations to assign them to the left and right axes.
        </div>
        <div
          v-for="(m, i) in dualAxisMetrics"
          :key="m.alias"
          class="dual-axis-row d-flex align-center mt-1"
          style="gap: 10px">
          <span class="text-caption text-truncate" style="flex: 1">{{ m.label || m.alias }}</span>
          <v-btn-toggle
            :value="dualAxisSide(m.alias, i)"
            mandatory
            dense
            @change="setDualAxisSide(m.alias, $event)">
            <v-btn small value="left"><v-icon x-small class="mr-1">mdi-format-horizontal-align-left</v-icon> Left</v-btn>
            <v-btn small value="right"><v-icon x-small class="mr-1">mdi-format-horizontal-align-right</v-icon> Right</v-btn>
          </v-btn-toggle>
        </div>

        <!-- Per-metric line style: a colour and a line type (solid/dashed/dotted). Left at
             the defaults each metric keeps its palette colour + a solid line. The v-select
             uses `attach` so its menu is not clipped by the wizard's scroll pane. -->
        <template v-if="dualAxisMetrics.length">
          <span class="viz-sub-label mt-3">Line style</span>
          <div
            v-for="m in dualAxisMetrics"
            :key="`style-${m.alias}`"
            class="dual-axis-row d-flex align-center mt-1"
            style="gap: 10px">
            <span class="text-caption text-truncate" style="flex: 1">{{ m.label || m.alias }}</span>
            <v-select
              :value="seriesLineType(m.alias)"
              :items="lineTypeOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              attach
              style="max-width: 120px"
              @change="setSeriesLineType(m.alias, $event)"></v-select>
            <color-picker-button
              :value="seriesColor(m.alias)"
              size="24"
              icon="mdi-circle"
              allow-clear
              @input="setSeriesColor(m.alias, $event)"></color-picker-button>
          </div>
        </template>
      </template>

      <!-- KPI (count, non-gauge) appearance: icon, status (auto thresholds / fixed tone / schema),
           unit, footnote. Extracted to its own step-child to keep this file within the line ceiling. -->
      <wizard-step-behavior-kpi
        v-if="config.messageType === 'count' && !isGaugeType"
        :config="config"
        :status-modes="statusModes"
        :kpi-directions="kpiDirections"
        :kpi-icon-options="kpiIconOptions"
        :kpi-tone-options="kpiToneOptions"
        :kpi-has-threshold="kpiHasThreshold"
        :kpi-caution-hint="kpiCautionHint"
        :kpi-critical-hint="kpiCriticalHint"
        :kpi-threshold-error="kpiThresholdError"
        :kpi-schema-select-items="kpiSchemaSelectItems"
        :kpi-schema-library-loading="kpiSchemaLibraryLoading"
        :kpi-schema-show-details="kpiSchemaShowDetails"
        :kpi-schema-show-band-badge="kpiSchemaShowBandBadge"
        :kpi-schema-unit-items="kpiSchemaUnitItems"
        :kpi-schema-band-preview="kpiSchemaBandPreview"
        :kpi-schema-is-custom="kpiSchemaIsCustom"
        :kpi-schema-missing-reference="kpiSchemaMissingReference"
        @schema-change="$emit('schema-change', $event)">
      </wizard-step-behavior-kpi>

      <!-- Gauge dial config (count gauge variant): the dial range + colored zones. Reuses
           the schema bands editor; the last band reaches the maximum. -->
      <template v-if="isGaugeType">
        <span class="viz-sub-label">Range</span>
        <div class="d-flex" style="gap: 12px">
          <div>
            <span class="viz-sub-hint">Minimum</span>
            <v-text-field
              v-model.number="config.gauge.min"
              type="number"
              placeholder="0"
              outlined
              dense
              hide-details
              class="kpi-threshold-field">
            </v-text-field>
          </div>
          <div>
            <span class="viz-sub-hint">Maximum</span>
            <v-text-field
              v-model.number="config.gauge.max"
              type="number"
              placeholder="100"
              outlined
              dense
              hide-details
              class="kpi-threshold-field">
            </v-text-field>
          </div>
        </div>
        <p v-if="gaugeConfigError" class="viz-threshold-error warning--text mt-2" role="alert">
          <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
          {{ gaugeConfigError }}
        </p>

        <span class="viz-sub-label mt-3">Colored bands</span>
        <schema-bands-editor v-model="config.gauge.bands"></schema-bands-editor>
        <span class="viz-sub-hint mt-2">
          Each band colors the dial from where the band above it ends up to its own boundary; the last band reaches the maximum.
        </span>

        <span class="viz-sub-label mt-3">Unit</span>
        <v-text-field
          v-model="config.unit"
          placeholder="yr"
          maxlength="8"
          outlined
          dense
          hide-details
          class="kpi-unit-field">
        </v-text-field>
      </template>

      <template v-if="isChartType">
        <v-switch
          v-model="config.legend.show"
          color="primary"
          hide-details
          dense
          inset
          class="mt-3 pt-0 viz-switch">
          <template #label>
            <span class="viz-check-label">Show legend</span>
          </template>
        </v-switch>
        <div v-if="config.legend.show" class="mt-2">
          <span class="viz-sub-label">Legend position</span>
          <v-select
            v-model="config.legend.position"
            :items="legendPositions"
            item-text="name"
            item-value="value"
            outlined
            dense
            hide-details
            class="viz-narrow-select">
          </v-select>
        </div>

        <!-- Series colors: today's palette (Default) or a range schema (charts have no
             'direction' — that is single-value KPI semantics, §4.1). Serialized as
             coloringSource:'schema' + schemaSelection, the SAME shape the count path
             writes; consumed by the ECharts adapter per the §4.7 matrix. -->
        <template v-if="chartSupportsColorSchema">
          <option-info label="Series colors" :help="optionHelp.seriesColors" spaced />
          <v-btn-toggle v-model="config.chartColoringMode" mandatory dense>
            <v-btn small :value="chartColoringModes.STATIC"><v-icon x-small class="mr-1">mdi-palette-outline</v-icon> Default palette</v-btn>
            <v-btn small :value="chartColoringModes.SCHEMA"><v-icon x-small class="mr-1">mdi-palette-swatch</v-icon> By schema</v-btn>
          </v-btn-toggle>

          <schema-picker
            v-if="config.chartColoringMode === chartColoringModes.SCHEMA"
            :schema-id="config.schemaId"
            :value-unit="config.schemaValueUnit"
            unit-hint="The unit this measure is in. It is converted onto the schema bands."
            :select-items="kpiSchemaSelectItems"
            :library-loading="kpiSchemaLibraryLoading"
            :show-details="kpiSchemaShowDetails"
            :show-band-badge="kpiSchemaShowBandBadge"
            :unit-items="kpiSchemaUnitItems"
            :band-preview="kpiSchemaBandPreview"
            :is-custom="kpiSchemaIsCustom"
            :missing-reference="kpiSchemaMissingReference"
            @schema-input="config.schemaId = $event"
            @schema-change="$emit('schema-change', $event)"
            @unit-input="config.schemaValueUnit = $event">
          </schema-picker>
        </template>
      </template>

      <template v-if="config.messageType === 'datagrid'">
        <v-switch
          v-model="config.exportEnabled"
          color="primary"
          hide-details
          dense
          inset
          class="mt-3 pt-0 viz-switch">
          <template #label>
            <span class="viz-check-label">Enable export</span>
          </template>
        </v-switch>
        <div class="mt-2">
          <span class="viz-sub-label">Column width</span>
          <v-select
            v-model="config.columnWidthMode"
            :items="[{ text: 'Auto fit', value: 'autoFit' }, { text: 'Fixed', value: 'fixed' }]"
            outlined
            dense
            hide-details
            class="viz-narrow-select">
          </v-select>
        </div>

        <!-- Default order (A2): a persisted default sort + row cap the grid applies on
             load; runtime widget filters override it. -->
        <div class="mt-3">
          <span class="viz-sub-label">Default sort</span>
          <div class="d-flex" style="gap: 8px; max-width: 420px">
            <v-select
              v-model="config.sort.column"
              :items="datagridSortColumns"
              item-text="displayName"
              item-value="name"
              placeholder="No default sort"
              clearable
              outlined
              dense
              hide-details
              style="flex: 2">
            </v-select>
            <v-select
              v-model="config.sort.direction"
              :items="[{ text: 'Ascending', value: 'asc' }, { text: 'Descending', value: 'desc' }]"
              :disabled="!config.sort.column"
              outlined
              dense
              hide-details
              style="flex: 1">
            </v-select>
          </div>
        </div>

        <div class="mt-3">
          <span class="viz-sub-label">Limit rows</span>
          <div class="d-flex" style="gap: 8px; max-width: 460px">
            <v-select
              v-model="config.topBottom.mode"
              :items="[{ text: 'Show all', value: null }, { text: 'Top', value: 'top' }, { text: 'Bottom', value: 'bottom' }]"
              outlined
              dense
              hide-details
              style="flex: 1">
            </v-select>
            <v-text-field
              v-if="config.topBottom.mode"
              v-model.number="config.topBottom.count"
              type="number"
              placeholder="10"
              outlined
              dense
              hide-details
              style="flex: 1">
            </v-text-field>
            <v-select
              v-if="config.topBottom.mode"
              v-model="config.topBottom.column"
              :items="datagridNumericColumns"
              item-text="displayName"
              item-value="name"
              placeholder="by column"
              outlined
              dense
              hide-details
              style="flex: 2">
            </v-select>
          </div>
          <span class="viz-sub-hint mt-1">
            Show only the top or bottom N rows by a numeric column — e.g. the 10 shortest remaining life. Runtime widget filters override this.
          </span>
        </div>
      </template>
    </section>

    <!-- 3D link + colour coding -->
    <section class="viz-section">
      <div class="viz-section__head">
        <span class="ds-field-label">
          3D Model
          <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
        </span>
      </div>
      <v-switch
        v-model="config.link3D"
        color="primary"
        hide-details
        dense
        inset
        class="mt-0 pt-0 viz-switch"
        @change="$emit('link3d-change', $event)">
        <template #label>
          <span class="viz-check-label">Link rows to the 3D viewer</span>
        </template>
      </v-switch>

      <div v-if="config.link3D && hasSpatialBindings" class="sql-view-coloring viz-coloring">
        <span class="viz-sub-label">3D Focus</span>
        <v-radio-group
          :value="spatialFocusMode"
          hide-details
          class="mt-1"
          @change="setSpatialFocusMode">
          <v-radio label="Use source defaults" value="source" />
          <v-radio label="Override for this widget" value="override" />
        </v-radio-group>

        <v-alert
          v-if="spatialFocusDiagnostic"
          type="warning"
          dense
          text
          class="mt-3 mb-0">
          {{ spatialFocusDiagnostic }}
        </v-alert>

        <v-switch
          :input-value="spatialIncludeOwningContext"
          color="primary"
          dense
          inset
          hide-details
          class="mt-3 viz-switch"
          @change="setSpatialIncludeOwningContext">
          <template #label>
            <span class="viz-check-label">Include owning context</span>
          </template>
        </v-switch>
        <div class="viz-sub-hint mt-1">
          Anomaly, CML, and Inspection POIs keep their owning component or tag gray.
        </div>

        <v-switch
          :input-value="spatialNoTargetFallbackEnabled"
          color="primary"
          dense
          inset
          hide-details
          class="mt-3 viz-switch"
          @change="setSpatialNoTargetFallback">
          <template #label>
            <span class="viz-check-label">Use Master Tag as fallback POI</span>
          </template>
        </v-switch>
        <div class="viz-sub-hint mt-1">
          Off keeps targetless visible anchors as gray context instead of coloring them as targets.
        </div>

        <template v-if="spatialFocusMode === 'override'">
          <span class="viz-sub-label mt-3">Highlight bindings</span>
          <v-select
            :value="spatialTargetBindingKeys"
            :items="spatialTargetItems"
            item-text="text"
            item-value="value"
            multiple
            chips
            small-chips
            outlined
            dense
            hide-details
            class="viz-coloring__select"
            @change="setSpatialTargetBindings" />

          <div
            v-for="binding in spatialFilteredTargetBindings"
            :key="`types:${binding.bindingKey}`"
            class="mt-3">
            <span class="viz-sub-label">{{ binding.label || binding.bindingKey }} types</span>
            <v-select
              :value="spatialTargetTagTypes(binding.bindingKey)"
              :items="binding.filter.values"
              multiple
              chips
              small-chips
              outlined
              dense
              hide-details
              class="viz-coloring__select"
              @change="setSpatialTargetTagTypes(binding.bindingKey, $event)" />
          </div>

          <span class="viz-sub-label mt-3">Context bindings</span>
          <v-select
            :value="spatialContextBindingKeys"
            :items="spatialContextItems"
            item-text="text"
            item-value="value"
            item-disabled="disabled"
            multiple
            chips
            small-chips
            outlined
            dense
            hide-details
            class="viz-coloring__select"
            @change="setSpatialContextBindings" />

        </template>
      </div>

      <!-- 3D colour coding (3a) — pick a column then assign a colour per unique value -->
      <div v-if="config.link3D" class="sql-view-coloring viz-coloring">
        <option-info label="Colour models by" :help="optionHelp.coloringColumn" />
        <v-select
          v-model="config.coloring.column"
          :items="colouringColumnItems"
          item-text="displayName"
          item-value="name"
          placeholder="Select a column"
          outlined
          dense
          hide-details
          clearable
          class="viz-coloring__select"
          @change="$emit('coloring-column-change', $event)">
        </v-select>

        <div v-if="config.coloring.column" class="mt-2">
          <div v-if="coloringValuesLoading" class="d-flex align-center text-caption text--disabled">
            <div class="d-flex justify-center flex-grow-1">
              <envizion-loader :size="48" :color="$vuetify.theme.dark ? '#2cc295' : '#03624c'" label="Loading values" />
            </div>
          </div>
          <div v-else-if="!coloringValues.length" class="text-caption text--disabled">
            No distinct values found for this column.
          </div>
          <div v-else class="sql-view-coloring-values">
            <div
              v-for="val in coloringValues"
              :key="String(val)"
              class="d-flex align-center justify-space-between py-1">
              <span class="text-caption text-truncate mr-2" style="max-width: 70%">{{ formatColoringValue(val) }}</span>
              <color-picker-button
                :value="config.coloring.valueColors[String(val)] || null"
                size="22"
                icon="mdi-circle"
                @input="setColoringValueColor(val, $event)">
              </color-picker-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Register point-of-interest (Step 4) — for a register-backed grid, declare which linked
           point is highlighted in 3D and the context it is shown over. Mirrors the "Colour models
           by" control above (same viz-coloring markup + design tokens). Only rendered when the view
           is register-backed and its register declares link columns (poiApplicable); the highlight
           select maps the presence-aware tri-state (absent → Off, never auto-inherit). -->
      <div v-if="config.link3D && poiApplicable" class="sql-view-coloring viz-coloring">
        <option-info label="Highlight point of interest" :help="optionHelp.poiHighlight" />
        <v-select
          :value="poiHighlightValue"
          :items="poiHighlightItems"
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details
          class="viz-coloring__select"
          @change="setPoiHighlight">
        </v-select>

        <!-- Which colour definition wins. A widget can carry BOTH a chart series schema and the
             property colours; before this control neither was authoritative, so the chart could
             paint a value one colour while the 3D beside it painted the same value another. Charts
             only: a grid has no series schema, so it has nothing to choose between. -->
        <template v-if="poiChartMode && poiHighlightActive">
          <option-info label="Highlight colours from" :help="optionHelp.highlightColorSource" spaced />
          <v-select
            :value="highlightColorSourceValue"
            :items="highlightColorSourceItems"
            item-text="text"
            item-value="value"
            item-disabled="disabled"
            placeholder="Choose which colours win"
            outlined
            dense
            hide-details
            class="viz-coloring__select"
            @change="setHighlightColorSource">
          </v-select>
        </template>

        <!-- Context + emphasis are GRID-only: the chart path paints additive always and renders no
             marker channel, so both would show as controls that cannot take effect. -->
        <template v-if="!poiChartMode">
          <option-info label="Context to show it over" :help="optionHelp.poiContext" spaced />
          <v-select
            :value="poiContextValue"
            :items="poiContextItems"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            class="viz-coloring__select"
            @change="setPoiContext">
          </v-select>
        </template>

        <!-- Emphasis is a paint-mode switch — nothing to dim without a highlight — so it appears
             only once a highlight is chosen. Deliberate: the two selects always render (matching the
             "two selects + one toggle" spec and the Off/Off default), only this toggle is gated. -->
        <v-switch
          v-if="poiHighlightActive && !poiChartMode"
          :input-value="poiDimRest"
          color="primary"
          hide-details
          dense
          inset
          class="mt-3 pt-0 viz-switch"
          @change="setPoiDimRest">
          <template #label>
            <span class="viz-check-label">Dim the rest of the asset</span>
            <v-tooltip top max-width="320">
              <template #activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  x-small
                  class="viz-switch__info"
                  aria-label="About dimming the rest of the asset"
                  v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ optionHelp.poiDim }}</span>
            </v-tooltip>
          </template>
        </v-switch>
      </div>
      <!-- The register's link settings could not be read (schema 403): say why the picker is missing. -->
      <div
        v-if="config.link3D && !poiApplicable && poiUnavailableNote"
        class="text-caption text--secondary mt-2"
        role="status">
        {{ poiUnavailableNote }}
      </div>
    </section>

    <section class="viz-section">
      <div class="viz-section__head">
        <span class="ds-field-label">
          About this widget
          <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
        </span>
      </div>
      <v-textarea
        v-model="config.aboutText"
        maxlength="2000"
        rows="3"
        auto-grow
        outlined
        dense
        counter="2000"
        hint="Optional explanation for readers. Calculation details are added automatically."
        persistent-hint>
      </v-textarea>
    </section>

    <!-- Refresh: how often the widget re-queries on the board. Its own small group in Behavior &
         finish; the widget's title/subtitle live at the top of the Build step. -->
    <section class="viz-section">
      <div class="viz-section__head">
        <span class="ds-field-label">
          Refresh
          <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
        </span>
      </div>
      <span class="viz-sub-label">Auto refresh</span>
      <v-select
        v-model="config.refreshIntervalMinutes"
        :items="refreshIntervals"
        item-text="text"
        item-value="value"
        outlined
        dense
        hide-details
        class="viz-narrow-select">
      </v-select>
    </section>
  </div>
</template>

<script>
/**
 * WizardStepBehavior — the wizard's third step ("Behavior & finish"): every type-specific
 * appearance control (KPI status/thresholds/schema, gauge dial, chart legend + series colors,
 * datagrid sort/export/width/top-bottom, widget size), the 3D link + colour coding, and the
 * auto-refresh interval. (The widget's title/subtitle live at the top of the Build step.)
 *
 * Purely presentational: it holds NO widget config of its own. `config` is the parent's single
 * source of truth, passed by reference; v-model bindings and the small helpers below mutate it in
 * place (which is what preserves entered values across steps and keeps the parent's save payload
 * unchanged). Everything that needs parent-only state — the coloring-column distinct-values fetch,
 * the schema lookup — is emitted back up (link3d-change, coloring-column-change, schema-change).
 * Progressive disclosure is driven by the is*Type / messageType flags: only the chosen display
 * type's group renders.
 */
import SchemaPicker from './SchemaPicker.vue';
import SchemaBandsEditor from './SchemaBandsEditor.vue';
import ColorPickerButton from '@/features/Share/components/ColorPickerButton.vue';
import WizardStepBehaviorKpi from './WizardStepBehaviorKpi.vue';
import OptionInfo from './OptionInfo.vue';
import {
  normalizePoiSource,
  normalizeHighlightColorSource,
  POI_SOURCE_NONE,
  POI_PAINT_MODE,
  HIGHLIGHT_COLOR_SOURCE,
} from '@/features/Dashboard/utils/poiConfig';
import {
  spatialFocusFallbackEnabled,
  spatialFocusNoTargetForFallback,
  spatialFocusOwningContextEnabled,
} from '@/features/Dashboard/utils/spatialFocusPolicy';

/**
 * Tooltip copy for the Behavior step's options. Kept in one frozen map so the wording is reviewable
 * together rather than scattered through the template.
 *
 * RULE: each entry explains what the option DOES. An entry that merely restates its label is noise
 * and should be deleted rather than written.
 */
const OPTION_HELP = Object.freeze({
  seriesColors:
    "Colours the chart's own bars and slices. “Default palette” gives every series the same colour; "
    + '“By schema” uses a shared, named colour schema so separate widgets agree with each other.',
  colorSchema:
    'A facility-wide named schema. Its values are fixed by whoever defined it, so a value in your '
    + 'data that the schema does not list falls back to a generated colour.',
  coloringColumn:
    'Builds one colour per distinct value found in this column, straight from your data. These are '
    + 'the colours the 3D model, the highlight and the 3D legend use.',
  highlightColorSource:
    'Which of the two colour definitions above wins when this widget paints the 3D. Pick the widget '
    + 'colour schema to match other widgets, or the 3D model properties to match this widget’s own '
    + '“Colour models by” column.',
  poiHighlight:
    'Off paints the whole linked asset in the row’s colour. Choosing a column instead paints just '
    + 'the point that column links each row to — the specific CML or anomaly — over that asset.',
  poiContext:
    'The backdrop the highlighted point is shown against while dimming. Off dims nothing.',
  poiDim:
    'On greys the surrounding asset so the point stands out. Off keeps the asset in its normal '
    + 'colour and paints the point on top of it.',
});

export default {
  name: 'WizardStepBehavior',

  components: {
    SchemaPicker,
    SchemaBandsEditor,
    ColorPickerButton,
    WizardStepBehaviorKpi,
    OptionInfo,
  },

  props: {
    // Shared widget configuration (the parent's single source of truth, mutated in place).
    config: { type: Object, required: true },
    // Frozen status/coloring vocabularies (owned by the parent so its serializers and these
    // toggles stay in lockstep). Passed down for the template's v-model values + v-if branches.
    statusModes: { type: Object, required: true },
    kpiDirections: { type: Object, required: true },
    chartColoringModes: { type: Object, required: true },
    // Display-type flags driving progressive disclosure.
    isStackedType: { type: Boolean, default: false },
    isDualAxisType: { type: Boolean, default: false },
    isGaugeType: { type: Boolean, default: false },
    isChartType: { type: Boolean, default: false },
    chartSupportsColorSchema: { type: Boolean, default: false },
    // Dual-axis line: the aliased metrics offered for per-axis assignment + line styling.
    dualAxisMetrics: { type: Array, default: () => [] },
    lineTypeOptions: { type: Array, default: () => [] },
    // KPI (count) appearance options + inline validation.
    kpiIconOptions: { type: Array, default: () => [] },
    kpiToneOptions: { type: Array, default: () => [] },
    kpiHasThreshold: { type: Boolean, default: false },
    kpiCautionHint: { type: String, default: '' },
    kpiCriticalHint: { type: String, default: '' },
    kpiThresholdError: { type: String, default: null },
    gaugeConfigError: { type: String, default: null },
    // Schema picker inputs (built by the parent, forwarded verbatim to both SchemaPicker uses).
    kpiSchemaSelectItems: { type: Array, default: () => [] },
    kpiSchemaLibraryLoading: { type: Boolean, default: false },
    kpiSchemaShowDetails: { type: Boolean, default: false },
    kpiSchemaShowBandBadge: { type: Boolean, default: false },
    kpiSchemaUnitItems: { type: Array, default: () => [] },
    kpiSchemaBandPreview: { type: Array, default: null },
    kpiSchemaIsCustom: { type: Boolean, default: false },
    kpiSchemaMissingReference: { type: Boolean, default: false },
    // Chart legend + datagrid option sources.
    legendPositions: { type: Array, default: () => [] },
    datagridSortColumns: { type: Array, default: () => [] },
    datagridNumericColumns: { type: Array, default: () => [] },
    // 3D colour coding: the column choices + the loaded distinct values (parent-fetched).
    colouringColumnItems: { type: Array, default: () => [] },
    coloringValues: { type: Array, default: () => [] },
    coloringValuesLoading: { type: Boolean, default: false },
    // Current server-owned binding vocabulary. It is used for authoring only and is never stored
    // in the widget config; only a revision-pinned override may be persisted.
    spatialCapability: { type: Object, default: null },
    // Register POI picker (Step 4): whether the control applies (register-backed grid whose register
    // declares links) + the highlight/context option lists the parent builds from the declarations.
    poiApplicable: { type: Boolean, default: false },
    // Fixed copy shown when the register's link settings cannot be read (schema 403); null = none.
    poiUnavailableNote: { type: String, default: null },
    poiHighlightItems: { type: Array, default: () => [] },
    poiContextItems: { type: Array, default: () => [] },
    // Auto-refresh interval choices.
    refreshIntervals: { type: Array, default: () => [] },
  },

  computed: {
    hasSpatialBindings() {
      const spatial = this.spatialCapability;
      return Number(spatial?.spatialContractVersion) === 2
        && Array.isArray(spatial?.bindings)
        && !!spatial?.defaultFocus;
    },

    spatialFocusMode() {
      return this.config.spatialFocus?.mode === 'override' ? 'override' : 'source';
    },

    spatialNoTargetFallbackEnabled() {
      return spatialFocusFallbackEnabled(this.spatialCapability, this.config.spatialFocus);
    },

    spatialIncludeOwningContext() {
      return spatialFocusOwningContextEnabled(this.spatialCapability, this.config.spatialFocus);
    },

    spatialFocusDiagnostic() {
      if (this.spatialCapability?.defaultFocusDiagnostic) {
        return 'The source default is ambiguous. Review its Default 3D Focus and republish it.';
      }
      if (this.config.spatialFocus?.mode === 'override'
        && this.config.spatialFocus.sourceRevision !== this.spatialCapability?.sourceRevision) {
        return 'This override is stale. Review the binding choices before saving.';
      }
      return null;
    },

    spatialTargetItems() {
      return (this.spatialCapability?.bindings || [])
        .filter(binding => (binding.allowedRoles || []).includes('target'))
        .map(binding => ({ text: binding.label || binding.bindingKey, value: binding.bindingKey }));
    },

    spatialTargetBindingKeys() {
      return (this.config.spatialFocus?.targets || []).map(selector => selector.bindingKey);
    },

    spatialFilteredTargetBindings() {
      const selected = new Set(this.spatialTargetBindingKeys);
      return (this.spatialCapability?.bindings || []).filter(binding => selected.has(binding.bindingKey)
        && binding.filter?.kind === 'tagType'
        && Array.isArray(binding.filter.values));
    },

    spatialContextBindingKeys() {
      return (this.config.spatialFocus?.contexts || []).map(selector => selector.bindingKey);
    },

    spatialContextItems() {
      const selectedTargets = new Map((this.config.spatialFocus?.targets || [])
        .map(selector => [selector.bindingKey, selector]));
      return (this.spatialCapability?.bindings || [])
        .filter(binding => (binding.allowedRoles || []).includes('context'))
        .map((binding) => {
          const target = selectedTargets.get(binding.bindingKey);
          return {
            text: binding.label || binding.bindingKey,
            value: binding.bindingKey,
            disabled: !!target && !(target.tagTypes || []).length,
          };
        });
    },

    // Register POI selects map the stored config's presence-aware tri-state to a display value.
    // These are READ-ONLY getters (no prop mutation) — the setters below write via $set. Reading
    // this.config here subscribes the getter to the config object's observer, so a later $set of an
    // initially-absent key still re-renders. ABSENT (normalizePoiSource → undefined) shows as Off,
    // NEVER "Register default": opening + saving an untouched widget can only ever write 'none'
    // (still off), never 'inherit' (which would turn highlighting on). Only an explicit pick does.

    /** Highlight select value: 'none' (Off) for an absent/none key, else 'inherit' or a column. */
    poiHighlightValue() {
      return normalizePoiSource(this.config.poiSource) ?? POI_SOURCE_NONE;
    },

    /** Context select value: same tri-state mapping as the highlight select. */
    poiContextValue() {
      return normalizePoiSource(this.config.contextSource) ?? POI_SOURCE_NONE;
    },

    /** True once a highlight is chosen — gates the context select + emphasis toggle (both inert
     *  without a highlight, so they are disclosed progressively like the coloring swatches). */
    poiHighlightActive() {
      return this.poiHighlightValue !== POI_SOURCE_NONE;
    },

    /** Emphasis toggle state. Default ON (dim) — matches today's paint (buildPoiPaint always dims);
     *  only an explicit 'additive' turns it off. */
    poiDimRest() {
      return normalizePoiSource(this.config.poiPaintMode) !== POI_PAINT_MODE.ADDITIVE;
    },

    /** Tooltip copy, exposed so the template can reference it by key. */
    optionHelp() {
      return OPTION_HELP;
    },

    /** True when this widget carries a usable chart series colour schema. */
    hasSeriesSchema() {
      return this.config.chartColoringMode === this.chartColoringModes.SCHEMA
        && !!this.config.schemaId;
    },

    /** True when "Colour models by" has a column, i.e. property colours exist to paint from. */
    hasPropertyColors() {
      return !!(this.config.coloring && this.config.coloring.column);
    },

    /**
     * Which colour definition governs the 3D paint. ABSENT stays absent (null) so a widget saved
     * before this control existed keeps painting exactly as it does today — opening the wizard must
     * not silently commit a choice the user never made.
     */
    highlightColorSourceValue() {
      return normalizeHighlightColorSource(this.config.highlightColorSource);
    },

    /**
     * Both options ALWAYS render; an unconfigured one is disabled WITH ITS REASON rather than hidden.
     * Hiding a source is what produced the original defect — two colour definitions and no visible
     * statement of which one won.
     */
    highlightColorSourceItems() {
      return [
        {
          value: HIGHLIGHT_COLOR_SOURCE.PROPERTY,
          text: this.hasPropertyColors
            ? '3D model properties'
            : '3D model properties — pick a “Colour models by” column first',
          disabled: !this.hasPropertyColors,
        },
        {
          value: HIGHLIGHT_COLOR_SOURCE.SCHEMA,
          text: this.hasSeriesSchema
            ? 'Widget colour schema'
            : 'Widget colour schema — set “Series colors” to By schema first',
          disabled: !this.hasSeriesSchema,
        },
      ];
    },

    /**
     * The POI block on a CHART shows the highlight select and the colour-source select only. Context
     * and the dim toggle stay grid-only: the chart path paints additive always and renders no marker
     * channel, so both controls would be visible and inert.
     */
    poiChartMode() {
      return this.isChartType;
    },
  },

  methods: {
    /* eslint-disable vue/no-mutating-props */
    // The helpers below read/write the shared config object (dual-axis axis/style maps and the
    // 3D colour map). They were moved verbatim from EditSqlViewWidget — this step's UI is their
    // sole consumer. $set / $delete keep the dynamic keys reactive.

    setSpatialFocusMode(mode) {
      if (mode !== 'override') {
        this.$set(this.config, 'spatialFocus', null);
        return;
      }
      const source = this.spatialCapability?.defaultFocus || {};
      this.$set(this.config, 'spatialFocus', {
        mode: 'override',
        sourceRevision: this.spatialCapability?.sourceRevision || null,
        targets: (source.targets || []).map(selector => ({
          bindingKey: selector.bindingKey,
          ...(selector.tagTypes?.length ? { tagTypes: [...selector.tagTypes] } : {}),
        })),
        contexts: (source.contexts || []).map(selector => ({
          bindingKey: selector.bindingKey,
          selection: selector.selection,
        })),
        noTarget: spatialFocusNoTargetForFallback(
          spatialFocusFallbackEnabled(this.spatialCapability),
        ),
        ...(spatialFocusOwningContextEnabled(this.spatialCapability)
          ? {}
          : { includeOwningContext: false }),
      });
    },

    ensureSpatialOverride() {
      if (this.config.spatialFocus?.mode !== 'override') this.setSpatialFocusMode('override');
      return this.config.spatialFocus;
    },

    setSpatialTargetBindings(bindingKeys) {
      const focus = this.ensureSpatialOverride();
      const existing = new Map((focus.targets || []).map(selector => [selector.bindingKey, selector]));
      this.$set(focus, 'targets', (bindingKeys || []).map((bindingKey) => {
        const selector = existing.get(bindingKey);
        return selector ? { ...selector } : { bindingKey };
      }));
      this.setSpatialContextBindings(this.spatialContextBindingKeys);
    },

    spatialTargetTagTypes(bindingKey) {
      const selector = (this.config.spatialFocus?.targets || [])
        .find(target => target.bindingKey === bindingKey);
      return selector?.tagTypes || [];
    },

    setSpatialTargetTagTypes(bindingKey, tagTypes) {
      const focus = this.ensureSpatialOverride();
      this.$set(focus, 'targets', (focus.targets || []).map((selector) => {
        if (selector.bindingKey !== bindingKey) return selector;
        return (tagTypes || []).length
          ? { bindingKey, tagTypes: [...new Set(tagTypes)] }
          : { bindingKey };
      }));
      this.setSpatialContextBindings(this.spatialContextBindingKeys);
    },

    setSpatialContextBindings(bindingKeys) {
      const focus = this.ensureSpatialOverride();
      const targets = new Map((focus.targets || []).map(selector => [selector.bindingKey, selector]));
      const contexts = (bindingKeys || []).flatMap((bindingKey) => {
        const sameTarget = targets.get(bindingKey);
        if (sameTarget && !(sameTarget.tagTypes || []).length) return [];
        return [{ bindingKey, selection: sameTarget ? 'remaining' : 'all' }];
      });
      this.$set(focus, 'contexts', contexts);
    },

    setSpatialNoTargetFallback(enabled) {
      const focus = this.ensureSpatialOverride();
      this.$set(focus, 'noTarget', spatialFocusNoTargetForFallback(enabled));
    },

    setSpatialIncludeOwningContext(enabled) {
      const focus = this.ensureSpatialOverride();
      if (enabled === false) {
        this.$set(focus, 'includeOwningContext', false);
      } else {
        this.$delete(focus, 'includeOwningContext');
      }
    },

    /** Writes a user-chosen colour for a value into the enum map (reactive on a dynamic key). */
    setColoringValueColor(val, hex) {
      const key = String(val);
      if (hex) {
        this.$set(this.config.coloring.valueColors, key, hex);
      } else {
        this.$delete(this.config.coloring.valueColors, key);
      }
    },

    /** Display label for a distinct value in the swatch list. */
    formatColoringValue(val) {
      if (val === null || val === undefined || val === '') return '(blank)';
      return String(val);
    },

    // Register POI setters. Fire ONLY on an explicit user pick (@change/@input), so an untouched
    // widget's absent keys are never written — that is what keeps the 15 seeded widgets byte-
    // identical. $set makes an initially-absent key reactive; buildWidgetPayload serialises these
    // only when `!= null`, so 'none' is a real "off" and absent stays legacy-off.

    /** Write the highlight source ('none' | 'inherit' | <column>). When a highlight is first turned
     *  ON and the paint mode is still absent, make it concrete ('dim' — the displayed default) so a
     *  later consumer that treats absent as additive (the study's shipping recommendation, see
     *  registerPoi.js) cannot silently flip this ON widget to additive. Only seeds when absent, so a
     *  user's explicit emphasis choice is never overwritten. */
    setPoiHighlight(source) {
      this.$set(this.config, 'poiSource', source);
      if (source !== POI_SOURCE_NONE && normalizePoiSource(this.config.poiPaintMode) === undefined) {
        this.$set(this.config, 'poiPaintMode', POI_PAINT_MODE.DIM);
      }
    },

    /** Write the context source ('none' | 'inherit' | <column>). */
    setPoiContext(source) {
      this.$set(this.config, 'contextSource', source);
    },

    /** Write which colour definition governs the 3D paint ('schema' | 'property'). */
    setHighlightColorSource(source) {
      this.$set(this.config, 'highlightColorSource', source);
    },

    /** Write the emphasis paint mode: ON → dim the rest of the asset, OFF → additive overlay. */
    setPoiDimRest(on) {
      this.$set(this.config, 'poiPaintMode', on ? POI_PAINT_MODE.DIM : POI_PAINT_MODE.ADDITIVE);
    },

    /** The axis side ('left'|'right') for a dual-axis metric, honoring the user's explicit choice
     *  then the default (metric index 1 -> right, else left). */
    dualAxisSide(alias, index) {
      const working = this.config.axisByMetric || {};
      const explicit = working[alias];
      if (explicit === 'left' || explicit === 'right') return explicit;
      return index === 1 ? 'right' : 'left';
    },

    /** Persist a dual-axis metric's axis side into the working map (reactive on a dynamic key). */
    setDualAxisSide(alias, side) {
      if (!this.config.axisByMetric || typeof this.config.axisByMetric !== 'object') {
        this.$set(this.config, 'axisByMetric', {});
      }
      this.$set(this.config.axisByMetric, alias, side === 'right' ? 'right' : 'left');
    },

    /** The working style blob for a dual-axis metric (never null — a fresh, non-reactive read). */
    seriesStyle(alias) {
      const working = this.config.seriesStyles || {};
      return working[alias] || {};
    },

    /** The chosen line color for a dual-axis metric ('#RRGGBB' or null = palette default). */
    seriesColor(alias) {
      const color = this.seriesStyle(alias).color;
      return typeof color === 'string' && color.trim() ? color.trim() : null;
    },

    /** The chosen line type for a dual-axis metric ('solid' default | 'dashed' | 'dotted'). */
    seriesLineType(alias) {
      const t = this.seriesStyle(alias).lineType;
      return (t === 'dashed' || t === 'dotted') ? t : 'solid';
    },

    /** Merge one field into a dual-axis metric's style blob (reactive on the dynamic alias key),
     *  preserving the sibling field so setting a color never drops the line type and vice-versa. */
    setSeriesStyle(alias, patch) {
      if (!this.config.seriesStyles || typeof this.config.seriesStyles !== 'object') {
        this.$set(this.config, 'seriesStyles', {});
      }
      const current = this.config.seriesStyles[alias] || {};
      this.$set(this.config.seriesStyles, alias, { ...current, ...patch });
    },

    /** Persist a dual-axis metric's line color (null clears it back to the palette default). */
    setSeriesColor(alias, color) {
      const hex = typeof color === 'string' && color.trim() ? color.trim() : null;
      this.setSeriesStyle(alias, { color: hex });
    },

    /** Persist a dual-axis metric's line type ('solid' | 'dashed' | 'dotted'). */
    setSeriesLineType(alias, lineType) {
      const t = (lineType === 'dashed' || lineType === 'dotted') ? lineType : 'solid';
      this.setSeriesStyle(alias, { lineType: t });
    },
  },
};
</script>

<style lang="scss" scoped>
// This step's config pane: the same flex-column + gap layout the parent applies to its inline
// steps (kept in a class, not inline, so v-show restores the stylesheet display — see the parent).
.wizard-step-behavior {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
  gap: 24px;
}

// Always-open section blocks (shared with the parent's inline steps; small, deliberate duplication
// so this child is self-contained rather than reaching into the parent's scoped styles).
.viz-section {
  flex: 0 0 auto;
  border: 1px solid #e2e6e4;
  border-radius: 8px;
  padding: 14px 16px 16px;
}

.viz-section__head {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  .ds-field-label {
    margin-bottom: 0;
  }
}

.viz-section__action {
  margin-left: auto;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.viz-check-label {
  font-size: 13px;
  font-weight: 500;
}

.viz-sub-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  opacity: 0.75;
}

// Muted one-line caption under a picker or field.
.viz-sub-hint {
  display: block;
  margin-top: 6px;
  font-size: 11.5px;
  opacity: 0.6;
}

.kpi-unit-field {
  max-width: 120px;
}

// KPI threshold number fields (Caution from / Critical from): narrow, with room for a
// negative decimal boundary (e.g. remaining life -0.6).
.kpi-threshold-field {
  max-width: 160px;
}

// Inline threshold validation message. Colour comes from Vuetify `warning--text`; this class
// only owns layout so the icon + text align.
.viz-threshold-error {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  font-size: 12px;
  line-height: 1.4;
}

.viz-coloring {
  margin-top: 10px;

  .viz-coloring__select {
    max-width: 340px;
  }
}

.viz-narrow-select {
  max-width: 340px;
}

.theme--dark {
  .viz-section {
    border-color: rgba(255, 255, 255, 0.12);
  }
}

.sql-view-coloring {
  .sql-view-coloring-values {
    max-height: 30vh;
    overflow-y: auto;
  }
}
</style>
